import React, { useEffect, useState } from "react";
import { Link } from 'react-router-dom';
import Slider from 'react-slick';
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";

const FeatureOne = () => {
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/categories/`);
        setCategories(res.data);
        setIsLoading(false);
      } catch (err) {
        console.error('failed to load categories', err);
        setIsLoading(false);
      }
    };
    loadCategories();

    // HIGHLIGHT PRODUCT IMAGES - CATEGORY CIRCLES
    const styleSheet = document.createElement("style");
    styleSheet.textContent = `
      .feature-item {
        margin: 10px;
        transition: transform 0.3s ease;
      }
      .feature-item:hover {
        transform: translateY(-5px);
      }
      .feature-item__thumb {
        width: 220px !important;
        height: 220px !important;
        border-radius: 50% !important;
        background: transparent !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        margin: 0 auto 15px !important;
        overflow: visible !important; /* Allow scale beyond container */
        border: none !important;
        box-shadow: none !important;
        padding: 5px !important;
        transition: all 0.5s cubic-bezier(0.165, 0.84, 0.44, 1) !important;
      }
      .feature-item:hover .feature-item__thumb {
        background: transparent !important;
        box-shadow: none !important;
      }
      .feature-item__image {
        width: 100% !important;
        height: 100% !important;
        object-fit: contain !important;
        transition: transform 0.5s cubic-bezier(0.165, 0.84, 0.44, 1) !important;
      }
      .feature-item:hover .feature-item__image {
        transform: scale(1.3) translateY(-10px); /* Massive pop effect */
      }
      .feature-item__content h6 {
        font-weight: 600 !important;
        color: #333 !important;
        margin-top: 10px !important;
      }
      .feature-item__link {
        width: 100% !important;
        height: 100% !important;
        display: block !important;
      }
      /* Remove slick default padding/borders if any */
      .slick-slide > div {
        outline: none !important;
      }
    `;
    document.head.appendChild(styleSheet);
    return () => {
      if (document.head.contains(styleSheet)) {
        document.head.removeChild(styleSheet);
      }
    };
  }, []);

  function SampleNextArrow(props) {
    const { className, onClick } = props;
    return (
      <button
        type="button"
        onClick={onClick}
        className={`${className} slick-next slick-arrow flex-center rounded-circle`}
      >
        <i className="ph ph-caret-right" />
      </button>
    );
  }

  function SamplePrevArrow(props) {
    const { className, onClick } = props;
    return (
      <button
        type="button"
        onClick={onClick}
        className={`${className} slick-prev slick-arrow flex-center rounded-circle`}
      >
        <i className="ph ph-caret-left" />
      </button>
    );
  }

  const settings = {
    dots: false,
    infinite: true,
    speed: 600,
    slidesToShow: 6,
    slidesToScroll: 1,
    nextArrow: <SampleNextArrow />,
    prevArrow: <SamplePrevArrow />,
    swipe: true,
    swipeToSlide: true,
    autoplay: true,
    autoplaySpeed: 3000,
    pauseOnHover: true,
    responsive: [
      {
        breakpoint: 1400,
        settings: {
          slidesToShow: 5,
        }
      },
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: 4,
        }
      },
      {
        breakpoint: 992,
        settings: {
          slidesToShow: 3,
        }
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 2,
          arrows: false
        }
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
          arrows: false
        }
      }
    ]
  };

  return (
    <section className="feature py-40" id="featureSection">
      <div className="container container-lg">
        <div className="section-heading mb-40 text-center">
          <motion.h2
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-8"
          >
            Explore Top Categories
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-gray-600"
          >
            Find the best products across our wide range of categories
          </motion.p>
        </div>

        <div className="position-relative arrow-center">
          <div className="feature-item-wrapper">
            {isLoading ? (
              <div className="text-center py-48">
                <div className="spinner-border text-main" role="status">
                  <span className="visually-hidden">Loading categories...</span>
                </div>
              </div>
            ) : (
              <Slider {...settings}>
                {categories.map((cat, index) => (
                  <motion.div
                    key={cat.id}
                    initial={{ opacity: 0, scale: 0.8, y: 20 }}
                    whileInView={{ opacity: 1, scale: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{
                      duration: 0.5,
                      delay: index * 0.1,
                      type: "spring",
                      stiffness: 100
                    }}
                  >
                    <div className="feature-item text-center">
                      <div className="feature-item__thumb">
                        <Link
                          to={`/shop?category=${cat.id}`}
                          className="feature-item__link"
                        >
                          <img
                            src={cat.image || "assets/images/thumbs/placeholder.webp"}
                            alt={cat.name}
                            className="feature-item__image"
                          />
                        </Link>
                      </div>
                      <div className="feature-item__content">
                        <h6 className="text-lg">
                          <Link to={`/shop?category=${cat.id}`}>
                            {cat.name}
                          </Link>
                        </h6>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </Slider>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeatureOne;
